import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { TrendingUp, DollarSign, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MarginManagement = () => {
  const { admin, getAuthHeader } = useAuth();
  const isSuperAdmin = admin?.role === 'super_admin';
  
  const [settings, setSettings] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [hybridSettings, setHybridSettings] = useState({
    passthrough_margin_percent: 5,
    value_added_margin_percent: 25
  });

  useEffect(() => {
    fetchSettings();
    fetchStrategies();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/margin-settings`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
      setSelectedStrategy(response.data.margin_strategy);
      setHybridSettings({
        passthrough_margin_percent: response.data.passthrough_margin_percent,
        value_added_margin_percent: response.data.value_added_margin_percent
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStrategies = async () => {
    try {
      const response = await axios.get(`${API}/admin/margin-strategies`, {
        headers: getAuthHeader()
      });
      setStrategies(response.data);
    } catch (error) {
      console.error('Failed to fetch strategies:', error);
    }
  };

  const handleSaveStrategy = async () => {
    if (!isSuperAdmin) {
      alert('Only super admins can update margin settings');
      return;
    }

    if (!selectedStrategy) {
      alert('Please select a margin strategy');
      return;
    }

    if (!window.confirm(
      'Changing margin strategy will affect future price calculations. ' +
      'You will need to recompute all pricing rules for the changes to take effect. Continue?'
    )) {
      return;
    }

    try {
      setSaving(true);
      await axios.put(
        `${API}/admin/margin-settings`,
        {
          margin_strategy: selectedStrategy,
          ...hybridSettings
        },
        { headers: getAuthHeader() }
      );
      
      alert('Margin strategy updated successfully! Please recompute all pricing rules for changes to take effect.');
      fetchSettings();
    } catch (error) {
      console.error('Failed to update settings:', error);
      alert('Failed to update margin settings');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getStrategyColor = (strategy) => {
    const colors = {
      all_costs: '#3b82f6',
      value_added: '#10b981',
      hybrid: '#f59e0b'
    };
    return colors[strategy] || '#666';
  };

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <p>Loading margin settings...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <TrendingUp size={32} style={{ color: '#059669' }} />
          Margin Management
        </h1>
        <p style={{ color: '#666', fontSize: '16px' }}>
          Configure how margins are calculated on visa service pricing. Choose the strategy that best fits your business model.
        </p>
      </div>

      {/* Current Strategy Badge */}
      {settings && (
        <div style={{
          padding: '16px 24px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
          color: 'white',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>Current Active Strategy</p>
            <h2 style={{ margin: 0, marginTop: '4px', fontSize: '24px' }}>
              {strategies.find(s => s.strategy === settings.margin_strategy)?.name || settings.margin_strategy}
            </h2>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '12px'
          }}>
            Last updated: {new Date(settings.last_updated).toLocaleString('en-IN')}
          </div>
        </div>
      )}

      {/* Warning Box */}
      <div style={{
        padding: '16px',
        borderRadius: '8px',
        background: '#fef3c7',
        border: '1px solid #fde68a',
        marginBottom: '32px',
        display: 'flex',
        gap: '12px'
      }}>
        <AlertCircle size={20} style={{ color: '#92400e', flexShrink: 0, marginTop: '2px' }} />
        <div>
          <p style={{ margin: 0, fontWeight: '600', color: '#78350f', marginBottom: '4px' }}>
            Important: Recompute Pricing Rules After Changes
          </p>
          <p style={{ margin: 0, color: '#92400e', fontSize: '14px' }}>
            Any change to margin strategy will only affect future price calculations. 
            You must click "Compute Best Price" on each pricing rule to apply the new strategy.
          </p>
        </div>
      </div>

      {/* Strategy Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '32px' }}>
        {strategies.map((strategy) => {
          const isSelected = selectedStrategy === strategy.strategy;
          const isCurrent = settings && settings.margin_strategy === strategy.strategy;
          
          return (
            <div
              key={strategy.strategy}
              onClick={() => isSuperAdmin && setSelectedStrategy(strategy.strategy)}
              style={{
                padding: '24px',
                borderRadius: '12px',
                border: `2px solid ${isSelected ? getStrategyColor(strategy.strategy) : '#e5e7eb'}`,
                background: isSelected ? `${getStrategyColor(strategy.strategy)}10` : 'white',
                cursor: isSuperAdmin ? 'pointer' : 'default',
                transition: 'all 0.2s',
                position: 'relative'
              }}
            >
              {isCurrent && (
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: '#10b981',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <CheckCircle2 size={14} />
                  Active
                </div>
              )}

              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: getStrategyColor(strategy.strategy),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
                }}>
                <DollarSign size={24} style={{ color: 'white' }} />
              </div>

              <h3 style={{ margin: 0, marginBottom: '8px', fontSize: '18px', color: '#111' }}>
                {strategy.name}
              </h3>
              <p style={{ margin: 0, marginBottom: '16px', color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
                {strategy.description}
              </p>

              <div style={{
                padding: '12px',
                borderRadius: '8px',
                background: '#f9fafb',
                marginBottom: '16px'
              }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#666', fontWeight: '600', marginBottom: '8px' }}>
                  💡 Best For:
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#374151' }}>
                  {strategy.suitable_for}
                </p>
              </div>

              {/* Example Calculation */}
              <div style={{
                padding: '16px',
                borderRadius: '8px',
                background: '#fefce8',
                border: '1px solid #fde047'
              }}>
                <p style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: '#854d0e', marginBottom: '12px' }}>
                  Example Calculation:
                </p>
                <div style={{ fontSize: '13px', color: '#78350f' }}>
                  <p style={{ margin: 0, marginBottom: '4px' }}>{strategy.example_calculation.explanation}</p>
                  <p style={{ margin: 0, marginTop: '8px', fontWeight: '600', fontSize: '16px', color: '#059669' }}>
                    Final Price: {formatCurrency(strategy.example_calculation.final_price)}
                  </p>
                </div>
              </div>

              {isSelected && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  borderRadius: '8px',
                  background: getStrategyColor(strategy.strategy),
                  color: 'white',
                  textAlign: 'center',
                  fontWeight: '600',
                  fontSize: '14px'
                }}>
                  ✓ Selected
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Hybrid Settings */}
      {selectedStrategy === 'hybrid' && (
        <div style={{
          padding: '24px',
          borderRadius: '12px',
          background: '#fef9e7',
          border: '2px solid #f9d84f',
          marginBottom: '32px'
        }}>
          <h3 style={{ margin: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp size={20} style={{ color: '#f59e0b' }} />
            Hybrid Strategy Settings
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#78350f' }}>
                Passthrough Margin % (Embassy/VFS)
              </label>
              <input
                type="number"
                value={hybridSettings.passthrough_margin_percent}
                onChange={(e) => setHybridSettings({
                  ...hybridSettings,
                  passthrough_margin_percent: parseFloat(e.target.value)
                })}
                min="0"
                max="100"
                step="0.1"
                disabled={!isSuperAdmin}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #fde68a',
                  fontSize: '16px'
                }}
              />
              <small style={{ color: '#92400e', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Small margin to cover admin work on government fees
              </small>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#78350f' }}>
                Value-Added Margin % (Service Costs)
              </label>
              <input
                type="number"
                value={hybridSettings.value_added_margin_percent}
                onChange={(e) => setHybridSettings({
                  ...hybridSettings,
                  value_added_margin_percent: parseFloat(e.target.value)
                })}
                min="0"
                max="100"
                step="0.1"
                disabled={!isSuperAdmin}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #fde68a',
                  fontSize: '16px'
                }}
              />
              <small style={{ color: '#92400e', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Higher margin on your actual service value
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {isSuperAdmin && (
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <button
            onClick={handleSaveStrategy}
            disabled={saving || selectedStrategy === settings?.margin_strategy}
            className="btn-primary"
            style={{
              minWidth: '200px',
              padding: '16px 32px',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center'
            }}
          >
            {saving ? (
              <>
                <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle2 size={20} />
                Save Margin Strategy
              </>
            )}
          </button>
        </div>
      )}

      {!isSuperAdmin && (
        <div style={{
          padding: '16px',
          borderRadius: '8px',
          background: '#fee2e2',
          border: '1px solid #fecaca',
          textAlign: 'center',
          color: '#991b1b'
        }}>
          Only Super Admins can modify margin settings
        </div>
      )}
    </div>
  );
};

export default MarginManagement;
